/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useCallback, memo, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { dotmapsError, dotmapsOrangeWarning } from '@constants/colors';
import {
  scheduleTypes,
  segmentDirections
} from '@constants/component-configs';
import { Icon, Popover } from '@mui';
import {
  getSegmentTitle,
  renderEmptyScheduleString,
  renderScheduleString,
  showScheduleExceedsWarning
} from '@utils/segment-schedule/common';
import {
  formatLocationDate,
  formatLocationDateTime
} from '@utils/segment-schedule/dates';
import {
  renderPopoverExceptionList,
  renderPopoverRecurrenceList
} from '@utils/segment-schedule/recurrence';
import { enDash } from '@utils/shared-utils';
import { useEntityTypeScheduleEnabled } from '@components/config/hooks';

import './segment-list.scss';

const SegmentListItem = ({
  checkAddressErrors,
  data,
  dataType,
  publicConfig = false,
  onClick,
  segment
}) => {
  // Popover state:
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [descriptionAnchorEl, setDescriptionAnchorEl] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAnchorEl, setScheduleAnchorEl] = useState(null);

  const schedule = useMemo(() => segment?.schedules?.[0] || null, [segment?.schedules]);

  const { segment_impact_type: segmentImpactTypes } = useSelector(state => state.dataTypes);

  const direction = useMemo(() => {
    const item = Object.keys(segmentDirections).find(key => segmentDirections[key].value === segment.direction);
    if (item) {
      return segmentDirections[item].label;
    }
    return 'Unknown';
  }, [segment.direction]);

  const impactType = useMemo(() => {
    const item = segmentImpactTypes[segment.impact_type];
    if (item) {
      return item.name;
    }
    return 'Unknown';
  }, [segment.impact_type, segmentImpactTypes]);

  const scheduleSection = useMemo(() => {
    const showWarning = showScheduleExceedsWarning(data.start_date, data.end_date, segment);
    const fontProps = {};
    if (showWarning) {
      fontProps.color = dotmapsOrangeWarning;
    }
    const hasData = !(isEmpty(data.start_date) || isEmpty(data.end_date));
    return (
      <div styleName={`schedule ${showWarning ? 'warning' : ''}`}>
        {hasData && <Icon aria-hidden="true" styleName="icon" {...fontProps}>schedule</Icon>}
        {hasData && isEmpty(segment.schedules) && renderEmptyScheduleString(data)}
        {!isEmpty(segment.schedules) && renderScheduleString(segment.schedules)}
        {showWarning &&
          <Icon aria-hidden="true" color={dotmapsOrangeWarning} styleName="icon">error</Icon>
        }
      </div>
    );
  }, [data, segment]);

  const popoverSchedule = useMemo(() => {
    if (!schedule) {
      return null;
    }

    const {
      start_date: startDate,
      end_date: endDate,
      exceptions,
      recurrences,
      all_day: allDay,
      type
    } = schedule;

    return (
      <div styleName="popover-schedule">
        {(type === scheduleTypes.recurring.value || allDay) &&
          <div styleName="heading">
            {formatLocationDate(startDate)}
            {enDash}
            {formatLocationDate(endDate)}
            {type === scheduleTypes.oneTime.value && allDay && <span>&nbsp;(all day)</span>}
          </div>
        }
        {type === scheduleTypes.oneTime.value && !allDay &&
          <div styleName="heading">
            {formatLocationDateTime(startDate)}
            {enDash}
            {formatLocationDateTime(endDate)}
          </div>
        }
        {type === scheduleTypes.recurring.value &&
          <div styleName="body">
            {renderPopoverRecurrenceList(recurrences)}
          </div>
        }
        {type === scheduleTypes.recurring.value &&
          <div styleName="footer">
            {renderPopoverExceptionList(exceptions)}
          </div>
        }
      </div>
    );
  }, [schedule]);

  const onDescriptionPopoverOpen = useCallback(event => {
    event.preventDefault();
    setDescriptionOpen(true);
    setDescriptionAnchorEl(event.currentTarget);
  }, [setDescriptionAnchorEl, setDescriptionOpen]);

  const onDescriptionPopoverClose = useCallback(() => {
    setDescriptionOpen(false);
  }, [setDescriptionOpen]);

  const onSchedulePopoverOpen = useCallback(event => {
    event.preventDefault();

    setScheduleOpen(true);
    setScheduleAnchorEl(event.currentTarget);
  }, [setScheduleAnchorEl, setScheduleOpen]);

  const onSchedulePopoverClose = useCallback(() => {
    setScheduleOpen(false);
  }, [setScheduleOpen]);

  const showErrorIcon = useMemo(() => {
    // If after save a global segment error is caught,
    // it means the 'from' or 'to' addreses are empty,
    // thus show the error icon.
    if (checkAddressErrors && (isEmpty(segment.display_from) || isEmpty(segment.display_to))) {
      return true;
    }
    // Or show it too if there's other kind of error on the 'from/to' fields:
    return segment.display_from_error || segment.display_to_error;
  }, [checkAddressErrors, segment.display_from, segment.display_to, segment.display_from_error, segment.display_to_error]);

  const scheduleEnabled = useEntityTypeScheduleEnabled(dataType, publicConfig);

  return (
    <div
      onClick={onClick}
      onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && onClick()}
      role="button"
      tabIndex={0}
      aria-label={`Expand location ${getSegmentTitle(segment)}`}
      styleName="segment-list-item"
    >
      <div styleName="heading">
        <div styleName="street">{getSegmentTitle(segment)}</div>
        {showErrorIcon && (
          <Icon role="img" color={dotmapsError} styleName="error" aria-label="Error">
            error
          </Icon>
        )}
        <Icon aria-hidden="true" styleName="icon">unfold_more</Icon>
      </div>
      {scheduleEnabled && <div styleName="field-divider" />}
      {scheduleEnabled && segment.description && (
        <div onMouseOver={onDescriptionPopoverOpen} onMouseOut={onDescriptionPopoverClose} styleName="description">
          <Icon aria-hidden="true" styleName="icon">notes</Icon>
          <div styleName="text">
            {segment.description}
          </div>
        </div>
      )}
      {scheduleEnabled && (
        <div styleName="body">
          {direction}
          <div styleName="separator">&bull;</div>
          {impactType}
        </div>
      )}
      {scheduleEnabled && (
        <div onMouseOver={onSchedulePopoverOpen} onMouseOut={onSchedulePopoverClose} styleName="footer">
          {scheduleSection}
        </div>
      )}
      {scheduleEnabled && segment.description && (
        <Popover
          open={descriptionOpen}
          style={{pointerEvents: 'none'}}
          anchorEl={descriptionAnchorEl}
          anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
          transformOrigin={{horizontal: 'left', vertical: 'top'}}
          onClose={onDescriptionPopoverClose}
        >
          <div styleName="segment-list-item-popover">
            <div styleName="title">Description</div>
            <div styleName="content">{segment.description}</div>
          </div>
        </Popover>
      )}
      {scheduleEnabled && !isEmpty(segment.schedules) && (
        <Popover
          open={scheduleOpen}
          style={{pointerEvents: 'none'}}
          anchorEl={scheduleAnchorEl}
          anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
          transformOrigin={{horizontal: 'left', vertical: 'top'}}
          onClose={onSchedulePopoverClose}
        >
          <div styleName="segment-list-item-popover">
            <div styleName="title">Schedule</div>
            <div styleName="content">{popoverSchedule}</div>
          </div>
        </Popover>
      )}
    </div>
  );
};

SegmentListItem.propTypes = {
  // If true, check if addresses are empty and show the error icon.
  checkAddressErrors: PropTypes.bool,
  data: PropTypes.object,
  dataType: PropTypes.string,
  onClick: PropTypes.func,
  publicConfig: PropTypes.bool,
  segment: PropTypes.object
};

export default memo(SegmentListItem);
